import React, { useState, useEffect } from "react";
import { FaEdit, FaSave, FaTrash, FaUserPlus,FaCheckCircle,FaTimesCircle } from "react-icons/fa";
import styled from "styled-components";
import axios from "axios";
import AdminNavbar from "./AdminNavbar";
import toast, { Toaster } from "react-hot-toast";


const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom right, #f9f9f9, #e9eef2);
  font-family: "Inter", sans-serif;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  background-color: #f5f5f5;
  overflow-y: auto;
`;

const TableWrapper = styled.div`
  background-color: #fff;
  padding: 20px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    border-bottom: 1px solid #ddd;
    padding: 10px;
    text-align: left;
  }

  th {
    background-color: #f4f4f4;
  }

  input,
  select {
    width: 100%;
    padding: 5px;
    font-size: 14px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;

  button {
    background: none;
    border: none;
    cursor: pointer;
  }

  .edit {
    color: #4a90e2;
  }

  .save {
    color: #27ae60;
  }

  .delete {
    color: #e74c3c;
  }
`;

const AddUserButton = styled.button`
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 5px;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 10px;

  svg {
    margin-right: 5px;
  }
`;

const StickyFilterBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f9f9f9;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  padding: 10px;
  display: flex;
  gap: 10px;
  margin-bottom: 10px;

  input {
    padding: 8px;
    font-size: 14px;
    border: 1px solid #ddd;
    border-radius: 4px;
    flex: 1;
  }
`;

const AdminUserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(null); // Row in edit mode
  const [editUserData, setEditUserData] = useState({});
  const [newUser, setNewUser] = useState(null); // ✅ Set to null initially
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filters, setFilters] = useState({
    userID: "",
    userEmail: "",
    userRole: "",
    userCompany: "",
  });

  const licenseOptions = {
    BASIC_DEMO: [
      { label: "BASIC_DEMO - bez końca (99 lat)", value: "BASIC_DEMO", years: 99, color: "#607D8B" },
    ],
    DEMO: [
      { label: "DEMO BASIC - 7 dni", value: "DEMO BASIC - 7 dni", days: 7, color: "#FFC107" }, 
      { label: "DEMO EXTENDED - 15 dni", value: "DEMO EXTENDED - 15 dni", days: 15, color: "#FF9800" },
    ],
    STANDARD: [
      { label: "1 miesiąc", value: "STANDARD - 1 miesiąc", months: 1, color: "#2196F3" },
      { label: "6 miesięcy", value: "STANDARD - 6 miesięcy", months: 6, color: "#1976D2" },
      { label: "1 rok", value: "STANDARD - 1 rok", years: 1, color: "#0D47A1" },
    ],
    PRO: [
      { label: "1 miesiąc", value: "PRO - 1 miesiąc", months: 1, color: "#4CAF50" },
      { label: "6 miesięcy", value: "PRO - 6 miesięcy", months: 6, color: "#388E3C" },
      { label: "1 rok", value: "PRO - 1 rok", years: 1, color: "#1B5E20" },
    ],
    ENTERPRISE: [
      { label: "1 miesiąc", value: "ENTERPRISE - 1 miesiąc", months: 1, color: "#9C27B0" },
      { label: "6 miesięcy", value: "ENTERPRISE - 6 miesięcy", months: 6, color: "#7B1FA2" },
      { label: "1 rok", value: "ENTERPRISE - 1 rok", years: 1, color: "#4A148C" },
    ],
    NO_EXPIRE: [
      { label: "Bez końca (99 lat)", value: "NO EXPIRE", years: 99, color: "#FF5722" },
    ],
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get("https://server229256.nazwa.pl/api/users");
        setUsers(response.data);
        setFilteredUsers(response.data); // Synchronize filtered users
      } catch (err) {
        setError("Failed to fetch users.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Input Change Handler
  const handleInputChange = (e, key) => {
    setEditUserData({ ...editUserData, [key]: e.target.value });
  };

  const handleNewUserChange = (e, key) => {
    setNewUser({ ...newUser, [key]: e.target.value });
  };

  // Add New User
  const handleAddNewUser = () => {
    if (!newUser) {
      setNewUser({
        userID: "",
        userEmail: "",
        userRole: "USER",
        userCompany: "",
        userPhone: "",
        licenseType: "",
        licenseExpire: "",
        status: false, // ✅ Default new user to active
      });
    }
  };
  

  // Save New User
  const handleSaveNewUser = async () => {
    try {
      const response = await axios.post(
        "https://server229256.nazwa.pl/api/users",
        newUser
      );
      setUsers([response.data, ...users]);
      setNewUser(null);
      toast.success("Nowy użytkownik został dodany!");
    } catch (err) {
      toast.error("Nie udało się dodać użytkownika.");
    }
  };

const handleDelete = async (id, company) => {
  const ok = window.confirm("Czy na pewno chcesz usunąć tego użytkownika?");
  if (!ok) return;

  // ask if we should also drop company data
  const alsoDrop = window.confirm(
    "Usunąć również dane firmy (sprzedaż/zakupy/historie)? To działanie jest nieodwracalne."
  );

  try {
    const url = `https://server229256.nazwa.pl/api/users/${id}` +
                (alsoDrop ? "?drop=true&dropAll=false" : "");

    const { data } = await axios.delete(url);
    setUsers(users.filter((u) => u._id !== id));

    if (data?.drop?.dropped) {
      toast.success(
        data.drop.scope === "database"
          ? `Usunięto także bazę firmy ${data.company}.`
          : `Usunięto kolekcje firmy ${data.company} (${data.drop.count}).`
      );
    } else if (alsoDrop && data?.drop?.reason) {
      toast(`Nie usunięto danych firmy: ${data.drop.reason}`, { icon: "ℹ️" });
    } else {
      toast.success("Użytkownik został usunięty!");
    }
  } catch (err) {
    console.error("Error deleting user:", err.message);
    toast.error("Nie udało się usunąć użytkownika.");
  }
};

  // Enable Edit Mode
  const handleEdit = (user) => {
    setEditMode(user._id);
    setEditUserData(user);
  };

  // Save Edited User
  const handleSave = async (id) => {
    const selectedLicense = editUserData.licenseType;
    let expiryDate = editUserData.licenseExpire;
  
    // Recalculate if missing or invalid
    if (!expiryDate || new Date(expiryDate).toString() === "Invalid Date") {
      expiryDate = calculateExpiryDate(selectedLicense);
      if (!expiryDate) {
        toast.error("Nie udało się zaktualizować — problem z licencją.");
        return;
      }
    }
  
    const formattedUserData = {
      ...editUserData,
      licenseExpire: expiryDate,
    };
  
    try {
      const response = await axios.put(
        `https://server229256.nazwa.pl/api/users/${id}`,
        formattedUserData
      );
      setUsers(users.map((u) => (u._id === id ? response.data : u)));
      setEditMode(null);
      toast.success("Użytkownik został zaktualizowany!");
    } catch (err) {
      console.error("❌ Error updating user:", err.message);
      toast.error("Nie udało się zaktualizować użytkownika.");
    }
  };
  
  

const handleFilterChange = (e) => {
  const { name, value } = e.target;
  setFilters({ ...filters, [name]: value });
};


useEffect(() => {
  const filtered = users.filter((user) => {
    return (
      user.userID.toLowerCase().includes(filters.userID.toLowerCase()) &&
      user.userEmail.toLowerCase().includes(filters.userEmail.toLowerCase()) &&
      user.userRole.toLowerCase().includes(filters.userRole.toLowerCase()) &&
      user.userCompany.toLowerCase().includes(filters.userCompany.toLowerCase())
    );
  });
  setFilteredUsers(filtered);
}, [users, filters]);



  const calculateExpiryDate = (licenseType) => {
    const today = new Date();
  
    const selectedOption = Object.values(licenseOptions)
      .flat()
      .find((option) => option.value === licenseType);
  
    if (!selectedOption) return "";
  
    if (selectedOption.days) today.setDate(today.getDate() + selectedOption.days);
    if (selectedOption.months) today.setMonth(today.getMonth() + selectedOption.months);
    if (selectedOption.years) today.setFullYear(today.getFullYear() + selectedOption.years);
  
    return today.toISOString().split("T")[0]; // ✅ Always returns YYYY-MM-DD
  };
  
  
  



  return (


    
    <Container>
           <AdminNavbar /> 
      <Content>

        <Toaster />
        <h2>Lista Użytkowników</h2>
  
        {/* Sticky Filter Bar */}
        <StickyFilterBar>
          <input
            type="text"
            name="userID"
            placeholder="Filtruj po ID Użytkownika"
            value={filters.userID}
            onChange={handleFilterChange}
          />
          <input
            type="text"
            name="userEmail"
            placeholder="Filtruj po Emailu"
            value={filters.userEmail}
            onChange={handleFilterChange}
          />
          <input
            type="text"
            name="userRole"
            placeholder="Filtruj po Roli"
            value={filters.userRole}
            onChange={handleFilterChange}
          />
          <input
            type="text"
            name="userCompany"
            placeholder="Filtruj po Firmie"
            value={filters.userCompany}
            onChange={handleFilterChange}
          />
        </StickyFilterBar>
  
        <AddUserButton onClick={handleAddNewUser}>
          <FaUserPlus /> Dodaj Użytkownika
        </AddUserButton>
  
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>ID Użytkownika</th>
                <th>Email</th>
                <th>Rola</th>
                <th>Firma</th>
                <th>Telefon</th> {/* ✅ New column for phone number */}
                <th>Kod</th>
                <th>License</th>
                <th>License expire</th>
                <th>Status</th> {/* ✅ New column for user status */}
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {/* New User Row */}
              {newUser && (
  <tr>
    <td>
      <input
        type="text"
        value={newUser.userID}
        onChange={(e) => handleNewUserChange(e, "userID")}
      />
    </td>
    <td>
      <input
        type="text"
        value={newUser.userEmail}
        onChange={(e) => handleNewUserChange(e, "userEmail")}
      />
    </td>
    <td>
      <select
        value={newUser.userRole}
        onChange={(e) => handleNewUserChange(e, "userRole")}
      >
        <option value="SUPERADMIN">SUPERADMIN</option>
        <option value="INFLUENCER">INFLUENCER</option>
        <option value="ADMIN">ADMIN</option>
        <option value="USER">USER</option>
      </select>
    </td>
    <td>
      <input
        type="text"
        value={newUser.userCompany}
        onChange={(e) => handleNewUserChange(e, "userCompany")}
      />
    </td>
    <td>
      <input
        type="text"
        value={newUser.userPhone || ""}
        onChange={(e) => handleNewUserChange(e, "userPhone")}
      />
    </td>
    <td>
  <input
    type="text"
    value={newUser.Kodbonus || ""}
    onChange={(e) => handleNewUserChange(e, "Kodbonus")}
  />
</td>

    <td>
      <select
        value={newUser.licenseType || ""}
        onChange={(e) => {
          const selectedLicense = e.target.value;
          setNewUser({
            ...newUser,
            licenseType: selectedLicense,
            licenseExpire: calculateExpiryDate(selectedLicense),
          });
        }}
      >
        <option value="">Wybierz licencję</option>
        {Object.entries(licenseOptions).map(([category, licenses]) => (
          <optgroup label={category} key={category}>
            {licenses.map(({ label, value }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </optgroup>
        ))}
      </select>
    </td>
    <td>
      <input type="text" value={newUser.licenseExpire || ""} disabled />
    </td>
    <td>
      <select
        value={newUser.status}
        onChange={(e) => handleNewUserChange(e, "status")}
      >
        <option value="true">Aktywny</option>
        <option value="false">Nieaktywny</option>
      </select>
    </td>
    <td>
      <Actions>
        <button className="save" onClick={handleSaveNewUser}>
          <FaSave title="Zapisz" />
        </button>
      </Actions>
    </td>
  </tr>
)}

  
              {/* User Rows */}
              {filteredUsers.map((user) => (
                <tr key={user._id}>
{editMode === user._id ? (
  <>
    <td>
      <input
        value={editUserData.userID}
        onChange={(e) => handleInputChange(e, "userID")}
        disabled
      />
    </td>
    <td>
      <input
        value={editUserData.userEmail}
        onChange={(e) => handleInputChange(e, "userEmail")}
      />
    </td>
    <td>
      <select
        value={editUserData.userRole}
        onChange={(e) => handleInputChange(e, "userRole")}
      >
        <option value="SUPERADMIN">SUPERADMIN</option>
        <option value="INFLUENCER">INFLUENCER</option>
        <option value="ADMIN">ADMIN</option>
        <option value="USER">USER</option>
      </select>
    </td>
    <td>
      <input
        value={editUserData.userCompany}
        onChange={(e) => handleInputChange(e, "userCompany")}
      />
    </td>
    <td>
      <input
        value={editUserData.userPhone || ""}
        onChange={(e) => handleInputChange(e, "userPhone")}
      />
    </td>
    <td>
  <input
    value={editUserData.Kodbonus || ""}
    onChange={(e) => handleInputChange(e, "Kodbonus")}
  />
</td>

    <td>
      <select
        value={editUserData.licenseType || ""}
        onChange={(e) => {
            const selectedLicense = e.target.value;
            setEditUserData({
              ...editUserData,
              licenseType: selectedLicense,
              licenseExpire: calculateExpiryDate(selectedLicense), // ✅ Use correct function
            });
          }}
      >
        <option value="">Wybierz licencję</option>
        {Object.entries(licenseOptions).map(([category, licenses]) => (
          <optgroup label={category} key={category}>
            {licenses.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </td>
    <td>
      <input type="text" value={editUserData.licenseExpire || ""} disabled />
    </td>
    <td>
      <select
        value={editUserData.status}
        onChange={(e) => handleInputChange(e, "status")}
      >
        <option value="true">Aktywny</option>
        <option value="false">Nieaktywny</option>
      </select>
    </td>
    <td>
      <Actions>
        <button className="save" onClick={() => handleSave(user._id)}>
          <FaSave title="Zapisz" />
        </button>
      </Actions>
    </td>
  </>
) : (
  <>
    <td>{user.userID}</td>
    <td>{user.userEmail}</td>
    <td>{user.userRole}</td>
    <td>{user.userCompany}</td>
    <td>{user.userPhone || "—"}</td>
    <td>{user.Kodbonus || "—"}</td>

    <td>
      {user.licenseType ? (
        <span
          style={{
            backgroundColor: licenseOptions[
              Object.keys(licenseOptions).find((key) =>
                licenseOptions[key].some((opt) => opt.value === user.licenseType)
              )
            ]?.find((opt) => opt.value === user.licenseType)?.color || "#000",
            color: "white",
            padding: "5px",
            borderRadius: "5px",
            fontWeight: "bold",
          }}
        >
          {user.licenseType}
        </span>
      ) : (
        "—"
      )}
    </td>
    <td>
  {user.licenseExpire ? (
    <span
      style={{
        fontWeight: "bold",
        color: new Date(user.licenseExpire) > new Date() ? "green" : "red",
      }}
    >
      {new Date(user.licenseExpire).toLocaleDateString()}
    </span>
  ) : (
    "—"
  )}
</td>
    <td>
      {user.status ? (
        <FaCheckCircle color="green" title="Aktywny" />
      ) : (
        <FaTimesCircle color="red" title="Nieaktywny" />
      )}
    </td>
    <td>
      <Actions>
        <button className="edit" onClick={() => handleEdit(user)}>
          <FaEdit title="Edytuj" />
        </button>
<button className="delete" onClick={() => handleDelete(user._id, user.userCompany)}>
  <FaTrash title="Usuń" />
</button>

      </Actions>
    </td>
  </>
)}

                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Content>
    </Container>
  );
  
};

export default AdminUserList;
